/* ═══════════════════════════════════════════════════════════════
   moduleTagFilter — pure logic for the module browser's TAGS rail
   (see ModuleTagsRail.tsx): what tags are "in scope" for the modules
   currently shown by category, their FACETED counts once one or more
   tags are selected, the AND filter itself, and the first-12/"show
   all" chip split.

   Zero React, zero DOM — proven once here instead of three times by
   hand in the component.
   ═══════════════════════════════════════════════════════════════ */

import type { SpecModule } from '../../shared/spec-modules';

export interface TagCount {
  tag: string;
  count: number;
}

/** `m.tags || []` only guards a FALSY value — a truthy non-array (a JSON
 *  string a bad write left behind, a stray object) sails past that guard
 *  straight into `.map`/`.some` and throws. Every read in this file goes
 *  through here instead, so one bad row filters itself out of the tag
 *  universe rather than crashing the whole Tags rail. `ModuleService`
 *  already normalizes on the way in (`toModuleTagsArray`) — this is the
 *  second, cheap line of defense for any caller that didn't go through it. */
function safeTags(tags: unknown): string[] {
  return Array.isArray(tags) ? tags.filter((t): t is string => typeof t === 'string') : [];
}

/** Every tag across `modules` that has ALL of `selected` (case-insensitive,
 *  exact tag match) — the AND narrowing the Tags rail applies on top of
 *  whatever category scope / search the host already applied. Empty
 *  `selected` ⇒ every module unchanged. */
export function applyTagFilter<T extends Pick<SpecModule, 'tags'>>(
  modules: T[],
  selected: string[]
): T[] {
  if (!selected.length) return modules;
  const wanted = selected.map((t) => t.toLowerCase());
  return modules.filter((m) => {
    const have = new Set(safeTags(m.tags).map((t) => t.toLowerCase()));
    return wanted.every((t) => have.has(t));
  });
}

/** Every tag in `modules` (the category scope, BEFORE the tag filter),
 *  most-used first (ties alphabetical) — same tag universe regardless of
 *  `selected`, so "Tags (N)" never changes as chips are clicked.
 *
 *  Each chip's COUNT is faceted: a tag already in `selected` shows the size
 *  of the narrowed set (every selected tag shows the same number, since all
 *  of them must hold for a module to count) — including 0, so a tag that
 *  has stopped matching (e.g. the user drilled into a category where it
 *  no longer applies) stays visible with an honest zero instead of
 *  silently vanishing. A tag NOT in `selected` shows how many modules would
 *  match if it were ALSO selected (the narrowed set ∩ has-this-tag), so no
 *  chip ever promises rows that won't appear. */
export function tagsInScope(
  modules: Pick<SpecModule, 'tags'>[],
  selected: string[] = []
): TagCount[] {
  const selSet = new Set(selected.map((t) => t.toLowerCase()));
  const narrowed = selSet.size ? applyTagFilter(modules, selected) : modules;

  const universe = new Map<string, string>(); // lowercase key -> display spelling (first seen)
  for (const m of modules) {
    for (const raw of safeTags(m.tags)) {
      const key = raw.toLowerCase();
      if (!universe.has(key)) universe.set(key, raw);
    }
  }

  const out: TagCount[] = [];
  for (const [key, label] of universe) {
    const count = selSet.has(key)
      ? narrowed.length
      : narrowed.filter((m) => safeTags(m.tags).some((t) => t.toLowerCase() === key)).length;
    out.push({ tag: label, count });
  }
  return out.sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

/** Toggle `tag` in a selected-tags list, case-insensitively (keeps the
 *  spelling already in the list if it's a re-add of a differently-cased
 *  match; otherwise adds exactly what was clicked). */
export function toggleTagSelection(selected: string[], tag: string): string[] {
  const key = tag.toLowerCase();
  if (selected.some((s) => s.toLowerCase() === key)) {
    return selected.filter((s) => s.toLowerCase() !== key);
  }
  return [...selected, tag];
}

/** How many chips the Tags rail shows before collapsing the rest behind
 *  "Show all (N)". */
export const TAGS_RAIL_VISIBLE = 12;

/** Split a tag list into what's shown now vs. hidden behind "Show all". */
export function splitTagChips(
  tags: TagCount[],
  expanded: boolean
): { visible: TagCount[]; hiddenCount: number } {
  if (expanded || tags.length <= TAGS_RAIL_VISIBLE) return { visible: tags, hiddenCount: 0 };
  return {
    visible: tags.slice(0, TAGS_RAIL_VISIBLE),
    hiddenCount: tags.length - TAGS_RAIL_VISIBLE,
  };
}

/** Order the rail's chips: SELECTED tags pinned first, then by count desc,
 *  then name asc; catalog tags no module carries append last at count 0
 *  (case-insensitive de-dupe against used tags, used spelling wins). A
 *  selected tag that is in neither list stays visible at count 0. */
export function orderTagChips(
  counts: TagCount[],
  selected: string[],
  catalogTags: string[] = []
): TagCount[] {
  const seen = new Set(counts.map((c) => c.tag.toLowerCase()));
  const extras: TagCount[] = [];
  for (const raw of [...catalogTags, ...selected]) {
    const key = raw.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    extras.push({ tag: raw.trim(), count: 0 });
  }
  const byUsage = (a: TagCount, b: TagCount) => b.count - a.count || a.tag.localeCompare(b.tag);
  const all = [...counts].sort(byUsage).concat(extras.sort(byUsage));
  const sel = new Set(selected.map((t) => t.toLowerCase()));
  return [
    ...all.filter((c) => sel.has(c.tag.toLowerCase())),
    ...all.filter((c) => !sel.has(c.tag.toLowerCase())),
  ];
}

/** Case-insensitive substring filter for the rail's search box. */
export function filterTagChips(chips: TagCount[], query: string): TagCount[] {
  const q = query.trim().toLowerCase();
  if (!q) return chips;
  return chips.filter((c) => c.tag.toLowerCase().includes(q));
}

// ─── Tag-set math for the "Manage tags" dialog (rename / merge / delete) ───
// Tags live only on modules (`module_library.tags`), so every manager action
// is "rewrite the tag array of each module that carries the tag". These are
// the pure rewrites; `ModuleService.renameTag/mergeTag/deleteTag` apply them
// across modules.

export interface TagRewrite {
  /** The module's tags after the rewrite (normalized, case-insensitive de-dupe). */
  tags: string[];
  /** False when the module did not carry `from` — nothing to write. */
  changed: boolean;
}

const sameTag = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Replace `from` with `to` (case-insensitive match on `from`). Every other
 *  tag is kept; if the module already carries `to`, the two collapse into
 *  one (keeping the position of the first). Also the MERGE rewrite. */
export function renameTagIn(tags: unknown, from: string, to: string): TagRewrite {
  const list = safeTags(tags);
  if (!list.some((t) => sameTag(t, from))) return { tags: list, changed: false };
  const target = to.trim();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of list) {
    const next = sameTag(t, from) ? target : t;
    const key = next.toLowerCase();
    if (!next || seen.has(key)) continue;
    seen.add(key);
    out.push(next);
  }
  return { tags: out, changed: true };
}

/** Merge `from` into `into` — same rewrite as a rename onto an existing tag. */
export const mergeTagIn = renameTagIn;

/** Drop `tag` (case-insensitive); every other tag is kept. */
export function deleteTagIn(tags: unknown, tag: string): TagRewrite {
  const list = safeTags(tags);
  if (!list.some((t) => sameTag(t, tag))) return { tags: list, changed: false };
  return { tags: list.filter((t) => !sameTag(t, tag)), changed: true };
}
